import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useIsFetching } from "react-query"

import CloseIcon from "@mui/icons-material/Close"
import CodeIcon from "@mui/icons-material/Code"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined"
import { Grid, IconButton, Skeleton } from "@mui/material"
import {
  Button,
  Menu,
  MenuItem,
  NotificationUtils,
  RoundedTab,
  Snackbar,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"
import moment from "moment"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { KonanAvatar } from "../../components/core/Avatar"
import { MonacoTextEditor } from "../../components/data-display/MonacoTextEditor"
import { BaseTable } from "../../components/tables/BaseTable"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { DataFileLoader } from "../../pages/FilesView"
import { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import { FileTypeColorMap, FileTypeEnum } from "../DataFiles/interfaces"
import { FileHistory } from "./FileHistory"
import type { DataFileFormik, DetailViewProps } from "./Interfaces"

import styles from "./DetailView.module.scss"

/**
 * A detail view component for displaying a file's content.
 *
 * @param {DetailViewProps} props - The component props.
 * @param {number} props.fileSizeLimit - The max size of file content to display in MB.
 * @param {string} props.fileExtension - The file extension.
 * @param {Tab} props.tab - The current view of the JSON content.

 * @param {Dispatch<SetStateAction<Tab>>} props.setJsonViewTab - A function to update the current view of the JSON content.
 * @returns {ReactElement} The detail view component.
 */
export function DetailView(props: Readonly<DetailViewProps>): ReactElement {
  const {
    fileSizeLimit,
    tab,
    setTab: setJsonViewTab,
    handleEditFile,
    handleVersionRestore,
    isVersionRestoreLoading,
  } = props

  const { t } = useTranslation("datafiles")

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(true)
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const { values } = useFormikContext<DataFileFormik>()

  const downloadFileHandler = (fileURL: string): void => {
    const link = document.createElement("a")
    link.href = fileURL

    // Start download
    link.click()

    // Clean up and remove the link
    link.remove()
  }

  // calculate the height by subtracting the header, padding and margins, subheader,
  // tabs bar, snackbar and footer heights from the page height
  const monacoEditorHeight = `calc(100dvh - 85px - 24px - 72px - 67px - 68px - ${isSnackbarOpen ? "74px" : "0px"}`

  const isDataFileVersionFetching = useIsFetching({ queryKey: ["data-file-version"] })

  return (
    <div className={styles.root}>
      <div
        className={styles.mainContainer}
        style={{
          maxWidth: isVersionDialogOpen ? "calc(100% - 256px)" : "100%",
        }}
      >
        <header className={styles.headerContainer}>
          <div>
            <Typography variant="label-bold" gutterBottom>
              {t("detailView.fileName")}
            </Typography>
            <div className={styles.authorContainer}>
              {isDataFileVersionFetching || values.isDataFileVersionFetching ? (
                <Skeleton width={350} height={30} />
              ) : (
                <Fragment>
                  <Tooltip title={values.fileName}>
                    <Typography variant="h3-bold" noWrap style={{ maxWidth: "250px" }}>
                      {values.fileName}
                    </Typography>
                  </Tooltip>
                  {values.type && (
                    <Tag size="small" color={FileTypeColorMap[values.type as keyof typeof FileTypeColorMap]}>
                      {FileTypeEnum[values.type as keyof typeof FileTypeEnum]}
                    </Tag>
                  )}
                  {values.version && (
                    <Tag size="small" color={values.activeVersion === values.version ? "green" : "blue"}>
                      {values.version}
                    </Tag>
                  )}
                </Fragment>
              )}
            </div>
          </div>

          <div>
            <Typography variant="label-bold" gutterBottom>
              {t("detailView.author")}
            </Typography>
            <div className={styles.authorContainer}>
              {isDataFileVersionFetching || values.isDataFileVersionFetching ? (
                <Fragment>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton width={200} height={30} />
                </Fragment>
              ) : (
                <Fragment>
                  <KonanAvatar size={28} name={values.createdBy} />
                  <Typography variant="label-bold">{values.createdBy}</Typography>
                </Fragment>
              )}
            </div>
          </div>
          <div className={styles.infoBlockContainer}>
            <InfoBlock
              type="default"
              text={moment(values.createdAt).format("MMM DD YYYY h:mm A")}
              title={t("detailView.uploadedDate")}
              variantColor={2}
              isLoading={!!isDataFileVersionFetching || values.isDataFileVersionFetching}
              key={values.createdAt}
            />
          </div>

          <div className={styles.btnContainer}>
            {values.type === DataFileGroup.type.CONDITION_LIST && (
              <Fragment>
                {values.version && handleVersionRestore && values.version != values.activeVersion && (
                  <Button
                    variant="secondary"
                    onClick={() => handleVersionRestore(values.version as string)}
                    isLoading={isVersionRestoreLoading}
                  >
                    {t("detailView.restore")}
                  </Button>
                )}
                {!isVersionDialogOpen && (
                  <Button variant="secondary" onClick={() => setIsVersionDialogOpen((prev) => !prev)}>
                    {t("detailView.history")}
                  </Button>
                )}
              </Fragment>
            )}

            <IconButton
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
              aria-label="options-menu"
              size="small"
            >
              <MoreHorizIcon sx={{ color: "var(--base-text-2)" }} />
            </IconButton>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} menuMaxContent>
              <MenuItem
                onClick={() => {
                  navigator.clipboard.writeText(values.id)
                  NotificationUtils.toast(t("detailView.copied"), { severity: "success" })
                }}
                key={"download-menu-btn"}
              >
                <Typography variant="a" color={"base"} variantColor={2}>
                  {t("detailView.copyId")}
                </Typography>
              </MenuItem>

              <MenuItem onClick={() => downloadFileHandler(values.fileUrl)}>
                <Typography variant="a" color={"base"} variantColor={2}>
                  {t("detailView.downloadFile")}
                </Typography>
              </MenuItem>

              {values.type === DataFileGroup.type.CONDITION_LIST && (
                <MenuItem onClick={() => handleEditFile?.()}>
                  <Typography variant="a" color={"base"} variantColor={2}>
                    {t("detailView.uploadNewFile")}
                  </Typography>
                </MenuItem>
              )}
            </Menu>
          </div>
        </header>

        {isDataFileVersionFetching || values.isDataFileVersionFetching ? (
          <DataFileLoader />
        ) : (
          <Fragment>
            <Grid mb={2}>
              {isSnackbarOpen && (
                <Snackbar severity="warning" fullWidth removable onRemove={setIsSnackbarOpen.bind(null, false)}>
                  {t("detailView.snackbarLimit", { fileSizeLimit })}
                </Snackbar>
              )}
            </Grid>

            {values.fileExtension === "json" && (
              <Grid mb={2} container justifyContent={"space-between"} alignItems={"center"}>
                <Typography variant={"h3-bold"}>
                  {tab === "json" ? t("detailView.jsonView") : t("detailView.tableView")}
                </Typography>

                {/*TODO: remove this wrapper when added in SUI */}
                <div className={styles.tabsContainer}>
                  <Tabs value={tab} orientation="horizontal" rounded>
                    <RoundedTab
                      icon={() => <CodeIcon style={{ width: "16px", height: "16px" }} />}
                      label="JSON"
                      value={"json"}
                      selected={tab === "json"}
                      onClick={() => setJsonViewTab("json")}
                    />
                    <RoundedTab
                      icon={() => <TableRowsOutlinedIcon style={{ width: "16px", height: "16px" }} />}
                      label={t("detailView.tableView")}
                      value={"table"}
                      selected={tab === "table"}
                      onClick={() => setJsonViewTab("table")}
                    />
                  </Tabs>
                </div>
              </Grid>
            )}
            {!values.fileContent ? (
              <KonanEmptyState title={t("detailView.noData")} />
            ) : values.fileExtension === "csv" || tab === "table" ? (
              <Fragment>
                {Array.isArray(values.fileContent) && (
                  <BaseTable
                    data={values.fileContent}
                    isLoading={false}
                    columns={Object.keys(values.fileContent[0] ?? {}).map((key) => ({
                      accessorFn: (row: Record<PropertyKey, typeof key>) => row[key],
                      id: key,
                      header: key,
                    }))}
                    title={""}
                    showColumnFilters
                    disableToolbar
                    enableTableHead={values.type !== DataFileGroup.type.CONDITION_LIST}
                    enableOrdering={false}
                    filterInputBoxPlaceHolder={t("detailView.search")}
                  />
                )}

                {typeof values.fileContent !== "string" && !Array.isArray(values.fileContent) && (
                  <BaseTable
                    data={values.fileContent.formattedJsonData}
                    isLoading={false}
                    columns={values.fileContent.tableColumnKeys.map((key) => ({
                      accessorFn: (row: Record<PropertyKey, typeof key>) => row[key],
                      id: key.toString(),
                      header: key.toString(),
                    }))}
                    title={""}
                    showColumnFilters
                    disableToolbar
                    enableOrdering={false}
                    filterInputBoxPlaceHolder={t("detailView.search")}
                  />
                )}
              </Fragment>
            ) : (
              <MonacoTextEditor
                value={typeof values.fileContent === "string" ? values.fileContent : ""}
                language={"json"}
                height={monacoEditorHeight}
                readOnly
              />
            )}
          </Fragment>
        )}
      </div>

      {isVersionDialogOpen && (
        <div className={styles.sidebar}>
          <header>
            <Typography variant="h3-bold">{t("detailView.editHistory")}</Typography>
            <IconButton onClick={() => setIsVersionDialogOpen(false)} size="small" className={"close-icon-button"}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </header>

          <FileHistory />
        </div>
      )}
    </div>
  )
}
